'use client'

import { AnimatePresence, m } from 'framer-motion'
import { ArrowRight, Terminal } from 'lucide-react'
import React, { useState } from 'react'

// --- SHARED TYPES ---
interface FaqItem {
  q: string
  a: string
}

interface FaqProps {
  faq: FaqItem[]
  gradient?: string
}

// ============================================================================
// COMMAND CENTER - Sci-Fi Terminal with Typewriter Effect
// ============================================================================
export function FaqTerminal({ faq, gradient = 'from-emerald-400 to-cyan-400' }: FaqProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const [typedText, setTypedText] = useState('')

  const handleOpen = (index: number) => {
    if (openIndex === index) {
      setOpenIndex(null)
      setTypedText('')
    } else {
      setOpenIndex(index)
      setTypedText('')
      const answer = faq[index].a
      let i = 0
      const interval = setInterval(() => {
        if (i < answer.length) {
          setTypedText(answer.slice(0, i + 1))
          i++
        } else {
          clearInterval(interval)
        }
      }, 12)
    }
  }

  return (
    <div className="relative">
      {/* Terminal frame */}
      <div className="overflow-hidden rounded-3xl border border-emerald-500/20 bg-[#0d1117] shadow-2xl shadow-emerald-500/5">
        {/* Terminal Header */}
        <div className="flex items-center justify-between border-b border-white/5 bg-gradient-to-r from-[#161b22] to-[#0d1117] px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex items-center gap-3">
            <div className="h-3 w-3 rounded-full bg-red-500" />
            <div className="h-3 w-3 rounded-full bg-yellow-500" />
            <div className="h-3 w-3 rounded-full bg-green-500" />
          </div>
          <div className="flex items-center gap-2 font-mono text-base text-emerald-400/60 sm:gap-3 sm:text-[1.04rem]">
            <Terminal className="h-4 w-4" />
            <span className="hidden sm:inline">knowledge-base --mode interactive</span>
            <span className="sm:hidden">knowledge-base</span>
          </div>
          <div className="hidden items-center gap-2 sm:flex">
            <div className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
            <span className="font-mono text-[0.95rem] text-emerald-400/60">ONLINE</span>
          </div>
        </div>

        {/* Terminal Content */}
        <div className="space-y-0 p-4 font-mono sm:p-6 lg:p-8">
          {faq.map((item, i) => (
            <div key={i} className="border-b border-white/5 last:border-0">
              <button onClick={() => handleOpen(i)} className="group w-full py-6 text-left">
                <div className="flex items-start gap-3 sm:gap-4">
                  <span className="shrink-0 text-lg text-emerald-400 sm:text-xl">❯</span>
                  <span className="hidden shrink-0 text-cyan-400 sm:inline">faq.query</span>
                  <span className="hidden shrink-0 text-white/40 sm:inline">(</span>
                  <span className="min-w-0 flex-1 text-base leading-snug break-words whitespace-normal text-amber-300/90 transition-colors group-hover:text-amber-200 sm:text-[1.08rem]">
                    &quot;{item.q}&quot;
                  </span>
                  <span className="hidden shrink-0 text-white/40 sm:inline">)</span>
                  <ArrowRight
                    className={`mt-0.5 h-4 w-4 shrink-0 transform text-emerald-400 transition-transform ${openIndex === i ? 'rotate-90' : 'group-hover:translate-x-1'}`}
                  />
                </div>
              </button>

              <AnimatePresence>
                {openIndex === i && (
                  <m.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="mb-4 ml-2 border-l-2 border-emerald-500/30 pb-6 pl-6 sm:pl-10">
                      <div className="mb-2 text-[0.95rem] text-emerald-400/50">
                        {'// Response received:'}
                      </div>
                      <p className="text-base leading-relaxed text-emerald-100/80 sm:text-[1.05rem]">
                        {typedText}
                        <span className="ml-1 inline-block h-5 w-2 animate-pulse bg-emerald-400 align-middle" />
                      </p>
                    </div>
                  </m.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>

      {/* Decorative glow */}
      <div
        className={`absolute -inset-4 bg-gradient-to-r ${gradient} pointer-events-none opacity-10 blur-3xl`}
      />
    </div>
  )
}
